import { useEffect, useRef, useState } from 'react';
import { isAxiosError } from 'axios';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { SOURCE_SYSTEMS, sourceSystemLabel } from 'components/inboundReferrals/constants';
import Button from 'components/shared/Button';
import ButtonGroup from 'components/shared/ButtonGroup';
import CircularProgress from 'components/shared/CircularProgress';
import FieldLabel from 'components/shared/form/FieldLabel';
import { Select } from 'components/shared/form/InfiniteScrollDropdown';
import Input from 'components/shared/form/Input';
import InputGroup from 'components/shared/form/InputGroup';
import Tabs, { TabType } from 'components/Tabs';
import { useProfile } from 'context/profile';
import { PERMISSIONS } from 'models/Profile';
import { adminShowClient } from 'services/api/admin/clients';
import {
  indexPortalAutomationSettings,
  portalAutomationSettingQueryKeys,
  RetrievalMode,
  upsertPortalAutomationSetting,
} from 'services/api/portalAutomationSettings';
import {
  AdminPortalScript,
  adminPortalScriptQueryKeys,
  indexAdminPortalScripts,
  updateAdminPortalScript,
  UpdateAdminPortalScriptInput,
} from 'services/api/portalScripts';
import { useToastActions } from 'stores/toastStore';
import { colors } from 'styles/theme/colors';

// The retrieval-mode ladder: each mode falls back to the one below it.
type RetrievalModeOption = { id: RetrievalMode; name: string; description: string };

const RETRIEVAL_MODE_OPTIONS: RetrievalModeOption[] = [
  { id: 'manual', name: 'Manual', description: 'A person reads the portal and types the details in.' },
  {
    id: 'extension',
    name: 'Extension',
    description: "The Olio browser extension retrieves in the user's browser, with the manual form as fallback.",
  },
];

type SystemForm = { retrievalMode: RetrievalMode };
type Forms = Record<string, SystemForm>;

function deepEqual(a: unknown, b: unknown) {
  return JSON.stringify(a) === JSON.stringify(b);
}

// The API rides the admin_client permissions (Admin::PortalAutomationSettingPolicy /
// Admin::PortalScriptPolicy): adminClientView to read, adminClientEdit to write.
// View-only users get the page with every field disabled, per admin convention.
export default function PortalAutomationSettings() {
  const { id } = useParams<{ id: string }>();
  const { profile } = useProfile();
  const { addToast } = useToastActions();
  const queryClient = useQueryClient();
  const disabled = !profile.has(PERMISSIONS.adminClientEdit);

  const clientQuery = useQuery({
    queryKey: ['adminClient', id],
    queryFn: ({ signal }) => adminShowClient(id!, signal),
    enabled: !!id,
  });
  const settingsQuery = useQuery({
    queryKey: portalAutomationSettingQueryKeys.list(id!),
    queryFn: ({ signal }) => indexPortalAutomationSettings(id!, signal),
    enabled: !!id,
  });
  const scriptsQuery = useQuery({
    queryKey: adminPortalScriptQueryKeys.list(id!),
    queryFn: ({ signal }) => indexAdminPortalScripts(id!, signal),
    enabled: !!id,
  });

  const serverForms = useRef<Forms>({});
  const [forms, setForms] = useState<Forms>({});
  const [selectedTabId, setSelectedTabId] = useState<string>(SOURCE_SYSTEMS[0]);

  useEffect(() => {
    if (!settingsQuery.data) return;

    const built: Forms = {};
    for (const sourceSystem of SOURCE_SYSTEMS) {
      const setting = settingsQuery.data.data.find((s) => s.sourceSystem === sourceSystem);
      built[sourceSystem] = {
        retrievalMode: setting?.retrievalMode ?? 'manual',
      };
    }
    serverForms.current = built;
    setForms(built);
  }, [settingsQuery.data]);

  const isDirty = !deepEqual(forms, serverForms.current);

  const { mutate: save, isPending } = useMutation({
    mutationFn: async (updated: Forms) => {
      const dirty = Object.entries(updated).filter(([sys, form]) => !deepEqual(form, serverForms.current[sys]));

      await Promise.all(
        dirty.map(([sourceSystem, form]) =>
          upsertPortalAutomationSetting({
            clientId: id!,
            sourceSystem,
            retrievalMode: form.retrievalMode,
          })
        )
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: portalAutomationSettingQueryKeys.list(id!) });
      addToast({ text: 'Portal automation settings saved.' });
    },
    onError: (error) => {
      const message =
        isAxiosError(error) && error.response?.status === 403 && error.response.data?.message
          ? error.response.data.message
          : 'There was an error saving the portal automation settings.';
      addToast({ text: message });
    },
  });

  const handleDiscard = () => setForms({ ...serverForms.current });

  const tabs: TabType[] = SOURCE_SYSTEMS.map((sourceSystem) => ({
    label: sourceSystemLabel(sourceSystem),
    value: sourceSystem,
  }));
  const selectedTab = tabs.find((t) => t.value === selectedTabId);
  const selectedForm = forms[selectedTabId];

  if (settingsQuery.isPending || clientQuery.isPending) {
    return <CircularProgress style={{ margin: '48px auto', display: 'block' }} />;
  }

  return (
    <PageContainer>
      <Header>
        <HeaderTop>
          <TitleArea>
            <Title>Portal Automation</Title>
            <Subtitle>{clientQuery.data?.name}</Subtitle>
          </TitleArea>
          {!disabled && (
            <ButtonGroup>
              <Button variant='ghost' onClick={handleDiscard} disabled={!isDirty || isPending}>
                Discard Changes
              </Button>
              <Button data-testid='portal-automation-save' onClick={() => save(forms)} disabled={!isDirty || isPending}>
                Save Changes
              </Button>
            </ButtonGroup>
          )}
        </HeaderTop>
        {tabs.length > 1 && (
          <TabsWrapper data-testid='portal-automation-tabs'>
            <Tabs tabs={tabs} selectedTab={selectedTab} onTabClick={(tab) => setSelectedTabId(tab.value)} />
          </TabsWrapper>
        )}
      </Header>

      <Content>
        {settingsQuery.isError && <ErrorText>Portal automation settings failed to load.</ErrorText>}
        {selectedForm && id && (
          <SourceSystemSettings
            clientId={id}
            sourceSystem={selectedTabId}
            form={selectedForm}
            onChange={(updated) => setForms((prev) => ({ ...prev, [selectedTabId]: updated }))}
            scripts={(scriptsQuery.data ?? []).filter((s) => s.sourceSystem === selectedTabId)}
            disabled={disabled}
          />
        )}
      </Content>
    </PageContainer>
  );
}

type SourceSystemProps = {
  clientId: string;
  sourceSystem: string;
  form: SystemForm;
  onChange: (form: SystemForm) => void;
  scripts: AdminPortalScript[];
  disabled: boolean;
};

function SourceSystemSettings({ clientId, sourceSystem, form, onChange, scripts, disabled }: SourceSystemProps) {
  const label = sourceSystemLabel(sourceSystem);
  const mode = RETRIEVAL_MODE_OPTIONS.find((o) => o.id === form.retrievalMode) ?? RETRIEVAL_MODE_OPTIONS[0];

  return (
    <SystemSection data-testid={`portal-automation-${sourceSystem}`}>
      <InputGroup title='Retrieval Mode'>
        <Select
          inputId={`retrieval-mode-${sourceSystem}`}
          options={RETRIEVAL_MODE_OPTIONS}
          value={mode}
          onChange={(v) => v && onChange({ ...form, retrievalMode: v.id })}
          getOptionValue={(o) => o!.id}
          getOptionLabel={(o) => o!.name}
          isDisabled={disabled}
          formatOptionLabel={(o, meta) => {
            if (!o) return null;
            if (meta?.context === 'value') return o.name;
            return (
              <div>
                <div>{o.name}</div>
                <div style={{ fontSize: '0.9em', color: colors.black50 }}>{o.description}</div>
              </div>
            );
          }}
        />
      </InputGroup>

      <ScriptsBlock data-testid={`portal-scripts-${sourceSystem}`}>
        <FieldLabel>Recorded scripts</FieldLabel>
        {scripts.length === 0 && <MutedNote>No scripts recorded for {label} yet.</MutedNote>}
        {scripts.map((script) => (
          <ScriptRow key={script.id} clientId={clientId} script={script} disabled={disabled} />
        ))}
      </ScriptsBlock>
    </SystemSection>
  );
}

type ScriptRowProps = {
  clientId: string;
  script: AdminPortalScript;
  disabled: boolean;
};

// One recorded script: rename inline or archive/restore. Bodies never reach the
// admin surface — a script's steps change only by re-recording in the extension.
function ScriptRow({ clientId, script, disabled }: ScriptRowProps) {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(script.name);

  const queryClient = useQueryClient();
  const { addToast } = useToastActions();

  const mutation = useMutation({
    mutationFn: (patch: Omit<UpdateAdminPortalScriptInput, 'id'>) =>
      updateAdminPortalScript({ id: script.id, ...patch }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminPortalScriptQueryKeys.list(clientId) });
      setEditing(false);
    },
    onError: () => addToast({ text: 'There was an error updating the script.' }),
  });

  const archived = !!script.archivedAt;
  return (
    <ScriptRowContainer data-testid={`portal-script-${script.id}`} $archived={archived}>
      <ScriptMain>
        {editing ? (
          <ScriptRenameRow>
            <Input value={name} onChange={(e) => setName(e.target.value)} data-testid={`script-name-${script.id}`} />
            <Button
              size='small'
              onClick={() => name.trim() && mutation.mutate({ name: name.trim() })}
              disabled={mutation.isPending}>
              Save
            </Button>
            <Button size='small' variant='ghost' onClick={() => setEditing(false)}>
              Cancel
            </Button>
          </ScriptRenameRow>
        ) : (
          <>
            <ScriptName>{script.name}</ScriptName>
            {archived && <ArchivedBadge>Archived</ArchivedBadge>}
          </>
        )}
        <ScriptMeta>
          {script.user.email} · updated {new Date(script.updatedAt).toLocaleDateString()}
        </ScriptMeta>
      </ScriptMain>
      {!editing && !disabled && (
        <ScriptActions>
          <ScriptActionButton onClick={() => setEditing(true)} disabled={mutation.isPending}>
            Rename
          </ScriptActionButton>
          <ScriptActionButton
            onClick={() => mutation.mutate({ archived: !archived })}
            disabled={mutation.isPending}
            data-testid={`script-archive-${script.id}`}>
            {archived ? 'Restore' : 'Archive'}
          </ScriptActionButton>
        </ScriptActions>
      )}
    </ScriptRowContainer>
  );
}

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
`;

const Header = styled.div`
  flex: 0 0 auto;
  background: white;
  border-bottom: 1px solid ${colors.black10};
`;

const HeaderTop = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 24px 24px 16px;
`;

const TitleArea = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const Title = styled.h2`
  font-size: 24px;
  font-weight: 700;
  margin: 0;
  color: ${colors.black100};
`;

const Subtitle = styled.h3`
  font-size: 16px;
  font-weight: 700;
  margin: 0;
  color: ${colors.blue100};
`;

const TabsWrapper = styled.div`
  border-top: 1px solid ${colors.black10};
  padding: 0 24px;
`;

const Content = styled.div`
  flex: 1 1 auto;
  overflow-y: auto;
  padding: 24px;
`;

const SystemSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-width: 560px;
`;

const MutedNote = styled.p`
  font-size: 12px;
  color: ${colors.black50};
  margin: 0;
`;

const ScriptsBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 16px;
`;

const ScriptRowContainer = styled.div<{ $archived: boolean }>`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 8px 10px;
  border: 1px solid ${colors.black10};
  border-radius: 6px;
  opacity: ${({ $archived }) => ($archived ? 0.6 : 1)};
`;

const ScriptMain = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  min-width: 0;
`;

const ScriptName = styled.span`
  font-size: 13px;
  font-weight: 600;
  overflow-wrap: anywhere;
`;

const ScriptMeta = styled.span`
  flex-basis: 100%;
  font-size: 11px;
  color: ${colors.black50};
`;

const ScriptRenameRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const ArchivedBadge = styled.span`
  font-size: 11px;
  color: ${colors.black50};
  border: 1px solid ${colors.black25};
  border-radius: 10px;
  padding: 1px 8px;
`;

const ScriptActions = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 4px;
  flex-shrink: 0;
`;

const ScriptActionButton = styled.button`
  background: none;
  border: none;
  padding: 0;
  font-size: 12px;
  color: ${colors.blue100};
  cursor: pointer;
  white-space: nowrap;

  &:hover {
    text-decoration: underline;
  }

  &:disabled {
    color: ${colors.black25};
    cursor: default;
  }
`;

const ErrorText = styled.p`
  font-size: 14px;
  color: ${colors.red100};
  margin: 0;
`;
